import React, { useState } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import { Collapse } from 'reactstrap';

import env from 'Env';
import RelatedProductCard from 'Components/Product/ProductRelated/RelatedProductCard';
import useViewport from 'Hooks/useViewport';

const PRODUCT_DLC_PAGE_THRESHOLD = env.productDlcPageThreshold;

export default function RelatedProducts({
  product,
  visibleCardsNumber,
  crossPromoteDlcSection,
  allHits,
}) {
  const [open, setOpen] = useState(false);
  const { isBelowMd } = useViewport();

  const { base_game: baseGame } = product;

  const productCrossPromotedHits = get(product, 'crossPromotedHits', []);
  const productRelatedHits = get(product, 'relatedHits', []);

  let relatedHits = crossPromoteDlcSection ? productCrossPromotedHits : productRelatedHits;

  if (allHits) relatedHits = productCrossPromotedHits.concat(productRelatedHits);

  const totalRelated = relatedHits ? relatedHits.length : 0;
  let relatedStart;
  let relatedEnd;

  if (totalRelated > 0) {
    relatedStart = relatedHits ?
      relatedHits.slice(0, visibleCardsNumber) : [];

    if (totalRelated > (visibleCardsNumber - 1)) {
      relatedEnd = relatedHits ?
        relatedHits.slice(visibleCardsNumber, totalRelated) : [];
    }
  }

  if (isEmpty(relatedHits)) return false;
  if (isBelowMd) return false;

  let viewAllLink = `${product.type}/${product.slug}/dlc`;

  const dlc = product.relatedHits && product.relatedHits.filter(h => h.type === 'dlc');

  const editions = product.editions;

  if (editions.length) {
    const baseEdition = editions.find(e => !e.parent_slug);
    if (baseEdition) {
      viewAllLink = `${baseEdition.type}/${baseEdition.slug}/dlc`;
    }
  }

  if (baseGame) {
    viewAllLink = `${baseGame.type}/${baseGame.slug}/dlc`;
  }

  const dlcCountThreshold = product.type === 'dlc' ? PRODUCT_DLC_PAGE_THRESHOLD - 1 : PRODUCT_DLC_PAGE_THRESHOLD;

  if ((dlc || []).length < dlcCountThreshold) {
    viewAllLink = null;
  }

  return (
    <section className="related-product-container page-section-margin with-dots">
      {!isEmpty(relatedHits) &&
        <React.Fragment>
          <div className="title-tabs-container">
            <div className="title-container">
              <h2 className="section-title">
                {baseGame && product.type !== 'dlc' && !crossPromoteDlcSection &&
                  <FormattedMessage id="newproduct.related.title.dlc.base" defaultMessage="DLC for {gameName}" values={{ gameName: baseGame.name }} />
                }
                {baseGame && product.type === 'dlc' && !crossPromoteDlcSection &&
                  <FormattedMessage id="newproduct.related.title.dlc-game-name.base" defaultMessage="Other DLC for {gameName}" values={{ gameName: baseGame.name }} />
                }
                {!baseGame && product.type !== 'dlc' && !crossPromoteDlcSection &&
                  <FormattedMessage id="newproduct.related.title.dlc" defaultMessage="DLC for this game" />
                }
                {!baseGame && product.type === 'dlc' && !crossPromoteDlcSection &&
                  <FormattedMessage id="newproduct.related.title.dlc-game-name" defaultMessage="Other DLC for this game" />
                }

                {crossPromoteDlcSection && baseGame &&
                  <FormattedMessage id="newproduct.related.title.newdlcforbasegame" defaultMessage="New DLC for {gameName}" values={{ gameName: baseGame.name }} />
                }

                {crossPromoteDlcSection && !baseGame &&
                  <FormattedMessage id="newproduct.related.title.newdlcfor" defaultMessage="New DLC for this game" />
                }
              </h2>
            </div>
            {!!viewAllLink &&
              <div className="tabs-container">
                <div className="tab-button-container">
                  <Link to={`/${viewAllLink}`} className="btn product-tab-button active">
                    <FormattedMessage id="newproduct.related.title.all-dlc" defaultMessage="View all DLC" />
                  </Link>
                </div>
              </div>
            }
          </div>

          {relatedStart.map(relatedGame => (
            <RelatedProductCard key={relatedGame.product_id} hit={relatedGame} />
          ))}
        </React.Fragment>
      }
      {/* eslint-disable jsx-a11y/no-static-element-interactions */}
      {!isEmpty(relatedHits) &&
        <React.Fragment>
          <Collapse isOpen={open}>
            {!isEmpty(relatedHits) && relatedEnd &&
              relatedEnd.map(relatedGame => (
                <div key={relatedGame.sku}>
                  <RelatedProductCard
                    key={relatedGame.product_id}
                    hit={relatedGame}
                  />
                </div>
              ))
            }
          </Collapse>
          {relatedEnd && !isEmpty(relatedEnd) && !open &&
            <div className="text-center">
              <button className="btn see-all-btn" onClick={() => setOpen(true)}>
                {baseGame &&
                  <FormattedMessage id="newproduct.related.button.more.base" defaultMessage="See all DLC for {gameName}" values={{ gameName: baseGame.name }} />
                }
                {!baseGame &&
                  <FormattedMessage id="newproduct.related.button.more" defaultMessage="See all DLC for this game" />
                }
              </button>
            </div>
          }
        </React.Fragment>
      }
    </section>
  );
}

RelatedProducts.propTypes = {
  product: PropTypes.shape({
    productType: PropTypes.string,
    baseGame: PropTypes.shape({}),
    relatedHits: PropTypes.arrayOf(PropTypes.object),
  }),
  visibleCardsNumber: PropTypes.number,
  crossPromoteDlcSection: PropTypes.bool,
  allHits: PropTypes.bool,
};

RelatedProducts.defaultProps = {
  product: null,
  visibleCardsNumber: 3,
  crossPromoteDlcSection: false,
  allHits: false,
};
